// The balance, order, cash and platform metrics (spec/17 §3 M-04, M-05, M-12, M-16 … M-18, M-20 … M-22; F-ANL-01).
//
// Like the rest of the canonical metrics these are pure: the server reads the facts, these define the number. A
// metric whose source the Platform does not record yet (loyalty points, reservations, subscriptions, commission)
// still has its definition here, tested on fixed vectors, and the server hands it no facts — so it reports **empty**,
// never a stand-in (spec/17 §1.5).
import { roundHalfUpBigInt } from "../money/round.js";
import { medianSeconds } from "./activity-metrics.js";
import { ratioOf } from "./ledger-metrics.js";
/**
 * **M-04 Deferred revenue / liability** (spec/17 §3): the parts summed where they have data. `null` when no part has
 * any — never a total of zero made of unknowns.
 */
export function deferredRevenueOf(parts) {
    const known = [parts.passes, parts.walletCash, parts.walletBonus, parts.points, parts.reservationPrepayments].filter((value) => value !== null);
    if (known.length === 0)
        return null;
    return { parts, total: known.reduce((sum, value) => sum + value, 0n) };
}
/**
 * Unused pass seconds valued at their **effective price** (spec/17 M-04): each holding's remaining seconds × what it
 * sold for ÷ the seconds it carried, rounded half up. A granted (free) pass is a liability of zero money. `null` with
 * no holdings.
 */
export function passLiabilityOf(holdings) {
    if (holdings.length === 0)
        return null;
    let total = 0n;
    for (const holding of holdings) {
        if (holding.totalSeconds <= 0 || holding.remainingSeconds <= 0)
            continue;
        const remaining = Math.min(holding.remainingSeconds, holding.totalSeconds);
        total += roundHalfUpBigInt(BigInt(remaining) * holding.pricePaidMinor, BigInt(holding.totalSeconds));
    }
    return total;
}
/**
 * **M-05 Orders** (spec/17 §3): the count of placed orders, and average order value = the M-01 basis ÷ paid orders.
 * `null` when nothing was placed.
 */
export function ordersOf(placed, paid, revenueTotal) {
    if (placed === 0 && paid === 0)
        return null;
    const averageOrderValue = paid > 0 && revenueTotal !== null ? roundHalfUpBigInt(revenueTotal, BigInt(paid)) : null;
    return { placed, paid, averageOrderValue };
}
const DAY_MILLIS = 86_400_000;
/**
 * **M-12 On-account outstanding** (spec/17 §3): the sum of the players' `receivable` balances, aged 0–7, 8–30 and 31+
 * days. What a player paid settles their **oldest** debt first (first in, first out), so what remains is aged by
 * when it was raised. An overpaid player owes nothing (their credit is not netted against another's debt). `null`
 * with no movements at all.
 */
export function receivablesOutstandingOf(movements, asOf) {
    if (movements.length === 0)
        return null;
    const byPlayer = new Map();
    for (const movement of movements) {
        const list = byPlayer.get(movement.playerId) ?? [];
        list.push(movement);
        byPlayer.set(movement.playerId, list);
    }
    const aged = { days0to7: 0n, days8to30: 0n, days31plus: 0n };
    let outstanding = 0n;
    let players = 0;
    for (const list of byPlayer.values()) {
        let credit = list.filter((movement) => movement.amount < 0n).reduce((sum, movement) => sum - movement.amount, 0n);
        const debits = list.filter((movement) => movement.amount > 0n).sort((a, b) => a.at - b.at);
        let owed = 0n;
        for (const debit of debits) {
            const settled = credit >= debit.amount ? debit.amount : credit;
            credit -= settled;
            const left = debit.amount - settled;
            if (left <= 0n)
                continue;
            owed += left;
            const age = Math.floor((asOf - debit.at) / DAY_MILLIS);
            if (age <= 7)
                aged.days0to7 += left;
            else if (age <= 30)
                aged.days8to30 += left;
            else
                aged.days31plus += left;
        }
        if (owed > 0n)
            players += 1;
        outstanding += owed;
    }
    return { outstanding, players, aged };
}
/** **M-16 Cash variance** (spec/17 §3): per shift, counted minus expected by tender. `null` with no closed shift. */
export function cashVarianceOf(counts) {
    if (counts.length === 0)
        return null;
    const byShift = {};
    const byTender = {};
    let total = 0n;
    for (const count of counts) {
        const variance = count.counted - count.expected;
        byShift[count.shiftId] = (byShift[count.shiftId] ?? 0n) + variance;
        byTender[count.tender] = (byTender[count.tender] ?? 0n) + variance;
        total += variance;
    }
    return { total, byShift, byTender, shiftsWithVariance: Object.values(byShift).filter((value) => value !== 0n).length };
}
/**
 * **M-17 Loyalty** (spec/17 §3): points issued, redeemed, expired and outstanding, and the tier distribution. `null`
 * when there were no movements and nobody holds a tier — which is every organisation until loyalty ships (F-LOY).
 */
export function loyaltyOf(movements, outstanding, tierMembers) {
    if (movements.length === 0 && outstanding === null && Object.keys(tierMembers).length === 0)
        return null;
    const sum = (kind) => movements.filter((movement) => movement.kind === kind).reduce((total, movement) => total + movement.points, 0n);
    return { issued: sum('issued'), redeemed: sum('redeemed'), expired: sum('expired'), outstanding: outstanding ?? 0n, tiers: tierMembers };
}
/** **M-18 Reservation performance** (spec/17 §3). `null` with no reservations. */
export function reservationPerformanceOf(reservations) {
    if (reservations.length === 0)
        return null;
    const checkedIn = reservations.filter((row) => row.outcome === 'checked_in').length;
    const noShows = reservations.filter((row) => row.outcome === 'no_show').length;
    return {
        booked: reservations.length,
        checkedIn,
        noShows,
        noShowRate: ratioOf(BigInt(noShows), BigInt(checkedIn + noShows)),
        medianLeadSeconds: medianSeconds(reservations.map((row) => Math.max(0, Math.round((row.startsAt - row.bookedAt) / 1000)))),
        depositKept: reservations.reduce((sum, row) => sum + row.depositKeptMinor, 0n),
    };
}
/** **M-20 Subscription (HQ)** (spec/17 §3). `null` with no subscriptions — every deployment until F-HQ-07 ships. */
export function subscriptionsOf(subscriptions) {
    if (subscriptions.length === 0)
        return null;
    const paying = subscriptions.filter((row) => row.status === 'active' || row.status === 'past_due');
    const mrr = paying.reduce((sum, row) => sum + row.monthlyMinor, 0n);
    const atStart = subscriptions.filter((row) => row.activeAtStart);
    return {
        mrr,
        arpa: paying.length === 0 ? null : roundHalfUpBigInt(mrr, BigInt(paying.length)),
        churn: ratioOf(BigInt(atStart.filter((row) => row.status === 'cancelled').length), BigInt(atStart.length)),
        billableStations: paying.reduce((sum, row) => sum + row.billableStations, 0),
        overdue: subscriptions.filter((row) => row.status === 'past_due').length,
    };
}
/** **M-21 Commission (HQ)** (spec/17 §3): commission earned on online payments, by organisation. `null` with none. */
export function commissionOf(facts) {
    if (facts.length === 0)
        return null;
    const byOrganization = {};
    for (const fact of facts)
        byOrganization[fact.organizationId] = (byOrganization[fact.organizationId] ?? 0n) + fact.commissionMinor;
    return { total: Object.values(byOrganization).reduce((sum, value) => sum + value, 0n), byOrganization };
}
/**
 * **M-22 Turnover** (spec/17 §3): M-02 summed over every category, by GSTIN (by venue for a venue with none) and by
 * the tax mode, so a venue that changed mode in the period shows each part under its own mode. `null` with no days.
 */
export function turnoverOf(days) {
    if (days.length === 0)
        return null;
    const lines = new Map();
    for (const day of days) {
        const registration = day.gstin ?? `venue:${day.venueId}`;
        const key = `${registration}\u0000${day.taxMode}`;
        const line = lines.get(key) ?? { registration, taxMode: day.taxMode, turnover: 0n, days: 0 };
        line.turnover += day.netSales;
        line.days += 1;
        lines.set(key, line);
    }
    return [...lines.values()].sort((a, b) => (a.registration < b.registration ? -1 : a.registration > b.registration ? 1 : a.taxMode < b.taxMode ? -1 : a.taxMode > b.taxMode ? 1 : 0));
}
//# sourceMappingURL=business-metrics.js.map